/**
 * Partygoers. The Funrooms' hosts, and they have been waiting.
 *
 * Two things are agreed by every source and both are the behaviour:
 *
 * **The cheerfulness is a lure.** A partygoer that has noticed someone does not
 * hunt them; it walks toward them, laughing, at less than walking pace, and
 * keeps doing that until it is close enough. Nothing about the approach is
 * hostile and nothing about it is hidden. A player who has not met one before
 * will stand and watch it come, because it is the only thing in this building
 * that has ever seemed pleased to see them.
 *
 * **They are never alone.** Inside `revealRange` the act drops — one scream,
 * and it tells every other partygoer on the floor where the player is. It does
 * not matter how far away they are; "you can never catch a Partygoer alone" is
 * the line, and a pack that has to be within earshot of each other is not a
 * pack. What arrives is one thing running at you and, twenty seconds later,
 * the rest of them from the other direction.
 *
 * The counterplay is distance, and it is real: `sightRange` is 14 m, the
 * shortest of any hunter here, because the wiki is specific that they are
 * nearsighted. They cannot find a player who stays out past that and stays
 * quiet. What they have instead is the best hearing in the roster bar the
 * centipede, so anyone who moves normally will be found.
 *
 * (On speed the sources split. The community wiki calls them slow; every
 * Backrooms *game* makes them fast. The nearsightedness is kept from the wiki
 * because it is the more interesting claim and it is the counterplay; the speed
 * is taken from the games, because a thing that notices you and then ambles
 * over is not a threat.)
 */

import { TUNING } from '../../core/config';
import {
  driveAwareness,
  faceToward,
  holdStill,
  moveTo,
  setMode,
  sweepStep,
  takeHeardNoise,
  tryAttack,
  type Agent,
  type Behavior,
  type EntityWorld,
} from '../brain';
import { ENTITY_TUNING } from '../defs';
import { hearSignal, sightSignal } from '../perception';
import { VOICE } from '../voice';

const T = ENTITY_TUNING.partygoer;

/** Point its memory at a world position, allocating it once. */
function remember(a: Agent, x: number, z: number): void {
  const s = a.state;
  if (!s.lastKnown) s.lastKnown = { x, z };
  else {
    s.lastKnown.x = x;
    s.lastKnown.z = z;
  }
}

/**
 * Drop the act. One scream, and the whole floor is told.
 *
 * `a.counter` latches it so a partygoer that briefly loses the player and finds
 * them again does not re-announce; the reveal is a once-per-encounter event and
 * a stutter of screams would read as a bug.
 */
function reveal(a: Agent, w: EntityWorld): void {
  if (a.counter > 0) return;
  a.counter = 1;
  w.audio.play(VOICE.partygoerReveal, { pos: a.state.pos, gain: 1, rate: 0.62 });
  // Infinite radius on purpose. See the header.
  w.alertKin(a.state, 'partygoer', T.callLoudness, Infinity);
}

export const partygoerBehavior: Behavior = {
  kind: 'partygoer',

  spawn(a) {
    a.turnRate = 3.2;
    a.counter = 0;
    a.timer = a.rng.range(T.laughInterval[0], T.laughInterval[1]);
    a.timer2 = 0;
  },

  think(a, w) {
    const s = a.state;

    const signal = Math.max(
      sightSignal(a.def, s, w.grid, w.player, w.playerLight),
      hearSignal(a.def, s, w.grid, w.player),
    );
    driveAwareness(a, w, signal, T.gain, T.forget);

    // A call from another partygoer arrives exactly as a heard noise does, so
    // it is hearsay and not certainty — it still has to cross the floor and it
    // still has to find the player when it gets there.
    //
    // What it does buy is patience. An ordinary noise is worth
    // `searchDuration`, which at this thing's walking pace is twenty-three
    // metres of committed walking, and a floor is wider than that: measured
    // over sixteen called partygoers starting 54 to 63 m out, 9 arrived and the
    // other 7 gave up somewhere in the middle. At two and a half times that it
    // is 16 of 16. A call is not a noise it happened to hear — it is one of its
    // own telling it where to go, and it goes.
    const called = takeHeardNoise(a);
    if (called) {
      remember(a, called.x, called.z);
      s.awareness = Math.min(1, s.awareness + a.heardLoudness * 0.6);
      a.searchTimer = TUNING.entities.searchDuration * (a.heardLoudness >= T.callLoudness ? 2.5 : 1);
    }

    switch (s.mode) {
      case 'chase': {
        moveTo(a, w, s.lastKnown ?? w.player.pos, a.def.chaseSpeed);

        a.timer -= w.dt;
        if (a.timer <= 0) {
          a.timer = a.rng.range(T.chaseLaughInterval[0], T.chaseLaughInterval[1]);
          w.audio.play(VOICE.partygoerReveal, { pos: s.pos, gain: 0.8, rate: 0.68 });
          w.audio.play(VOICE.packStep, { pos: s.pos, gain: 0.6, rate: 1.15 });
        }

        if (s.distToPlayer <= a.def.attackRange) {
          setMode(a, w, 'attack');
          break;
        }
        // They are committed. Eight seconds of running with nothing to go on
        // before one of them will admit it has lost the thread.
        if (s.modeTime > T.chaseCommit && s.awareness < T.dropAwareness) {
          a.counter = 0;
          setMode(a, w, 'stalk');
        }
        break;
      }

      case 'attack': {
        faceToward(a, w.player.pos);
        holdStill(a);
        if (tryAttack(a, w)) {
          s.awareness = 1;
          remember(a, w.player.pos.x, w.player.pos.z);
        }
        w.dread(s, a.def.sanityDrain);
        if (s.distToPlayer > a.def.attackRange * 1.6) setMode(a, w, 'chase');
        break;
      }

      case 'stalk': {
        // The approach, with the act still on. Slower than a walking player, so
        // this is never how it catches anyone — it is how it gets close enough
        // to stop pretending.
        const target = s.lastKnown ?? w.player.pos;
        moveTo(a, w, target, a.def.walkSpeed);
        faceToward(a, w.player.pos);

        a.timer -= w.dt;
        if (a.timer <= 0) {
          a.timer = a.rng.range(T.laughInterval[0], T.laughInterval[1]);
          w.audio.play(VOICE.partygoerLaugh, { pos: s.pos, gain: 0.75, rate: 1.28 });
        }

        // Being looked at costs, because a thing walking toward you laughing is
        // worse than a thing running at you and the game should say so.
        if (s.visible) w.dread(s, a.def.sanityDrain * 0.7);

        if (s.distToPlayer <= T.revealRange) {
          reveal(a, w);
          setMode(a, w, 'chase');
          break;
        }
        if (s.awareness < T.dropAwareness && a.searchTimer <= 0) {
          s.lastKnown = null;
          a.counter = 0;
          setMode(a, w, 'wander');
        }
        break;
      }

      default: {
        setMode(a, w, 'wander');
        sweepStep(a, w, a.def.walkSpeed);
        if (s.awareness >= T.noticeAwareness && s.lastKnown) setMode(a, w, 'stalk');
        break;
      }
    }
  },
};
